import type { ApiResponse } from "@/types/api";
import { getClient } from "@/services/ApiService";

const secured = () => getClient();

interface IFortisTransactionUrlResponse {
  value: boolean;
  reason: string;
  id: number;
  url: string;
  channel: string;
}

export interface FortisTransactionUrlResponse extends ApiResponse {
  data: IFortisTransactionUrlResponse;
}

export interface FortisTransactionUrlRequest {
  facilityID: number;
  patientID: number;
  alias?: string;
  nameOnCard?: string;
  acctNum?: string;
  expDate?: string;
  cardCode?: string;
  amount: number;
  saveAlias: boolean;
  saveTitle: string;
  statementBatchID: number;
  email: string;
  taskID: number;
  processor?: string;
}

export interface PaymentProfileListRequest {
  facilityID: string | number;
  id: string | number;
  code?: string;
}

export interface IPaymentProfileListResponse {
  id: number;
  patientID: number;
  entryDate: string;
  description: string;
  paymentMethodID: number;
  paymentMethod: string;
  cc: boolean;
  eft: boolean;
  isDefault: boolean;
  nameOnAccount: string;
  routeNo: string;
  accountNo: string;
  expDate: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  processor: string;
  merchantPaymentMethodID: string;
  lastUpdate: string;
  declineCount: number;
  lastDecline: string;
  methodDesc: string;
  deleted: boolean;
  deletedDate: string;
  deletedUser: string;
  selected?: boolean;
}

export interface PaymentProfileListResponse extends ApiResponse {
  data: IPaymentProfileListResponse[];
}

interface CreditCard {
  selected: boolean;
  paymentMethod: string;
  accountNo: string;
  expDate?: string;
  month?: string;
  year?: string;
  merchantPaymentMethodID?: string;
  saveAlias?: boolean;
}

export interface IMakeStatementPaymentViaFortisRequest {
  facilityID: number;
  patientID: number;
  amount: number | string;
  statementBatchID: number | string;
  taskID: number | string;
  alias: CreditCard | string;
  nameOnCard?: string;
  acctNum?: string;
  expDate?: string;
  cardCode?: string;
  saveAlias?: boolean;
  saveTitle?: string;
  email?: string;
  processor?: string;
}

interface IMakeStatementPaymentViaFortisResponse {
  value: boolean;
  reason: string;
  id: number;
}

export interface MakeStatementPaymentViaFortisResponse extends ApiResponse {
  data: IMakeStatementPaymentViaFortisResponse;
}

export interface MakeStatementPaymentRequest {
  facilityID: number;
  patientID: number;
  alias?: string;
  nameOnCard?: string;
  acctNum?: string;
  expDate?: string;
  cardCode?: string;
  amount: number;
  saveAlias: boolean;
  saveTitle?: string;
  statementBatchID: number | string;
  email: string;
  taskID: number | string;
  processor?: string;
}

export interface IMakeStatementPaymentResponse {
  value: boolean;
  reason: string;
  id: number;
}

export interface MakeStatementPaymentResponse extends ApiResponse {
  data: IMakeStatementPaymentResponse;
}

const patientRequest = {
  request: {
    async getpatientfutureappts(
      facilityKey: string,
      patientKey: string
    ): Promise<ApiResponse> {
      return secured().get(
        `/Portal/GetPatientFutureAppts/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}`
      );
    },

    async gettasks(facilityKey: string, patientKey: string): Promise<ApiResponse> {
      return secured().get(
        `/Portal/GetTasks/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}`
      );
    },

    async getattachmentlist(
      facilityKey: string,
      patientKey: string
    ): Promise<ApiResponse> {
      return secured().get(
        `/Portal/GetAttachmentList/${encodeURIComponent(facilityKey)}/${encodeURIComponent(patientKey)}`
      );
    },

    async getPaymentProfileList(
      payload: PaymentProfileListRequest
    ): Promise<PaymentProfileListResponse> {
      return secured().post("/patientportal/getpaymentprofilelist", payload);
    },

    async insertpaymentprofile(payload: {
      facilityId: string;
      patientId: string;
      nameOnAccount: string;
      accountNo: boolean;
      expDate: Date;
      routeNo: string;
      address1: string;
      address2: string;
      city: string;
      state: string;
      zip: string;
      merchantPaymentMethodID: string;
      paymentMethodID: string;
      paymentMethod: string;
    }): Promise<ApiResponse> {
      return secured().post("/patientportal/insertpaymentprofile", payload);
    },

    async capturecardalias(payload: {
      facilityId: string;
      patientId: string;
      nameOnCard: string;
      acctNum: string;
      expDate: string; // MMYY format
      cardCode: string;
    }): Promise<ApiResponse> {
      return secured().post("/patientportal/capturecardalias", payload);
    },

    async deletepaymentprofile(payload: {
      facilityId: string;
      id: string;
      user: string;
    }): Promise<ApiResponse> {
      return secured().post("/patientportal/deletepaymentprofile", payload);
    },

    async downloadfilebase64(payload: {
      facilityId: string;
      id: string;
    }): Promise<ApiResponse> {
      return secured().post("/patientportal/downloadfilebase64", payload);
    },

    async makeStatementPayment(
      payload: MakeStatementPaymentRequest
    ): Promise<MakeStatementPaymentResponse> {
      return secured().post("/patientportal/manualsale", payload);
    },

    async makeStatementPaymentViaFortis(
      payload: IMakeStatementPaymentViaFortisRequest
    ): Promise<MakeStatementPaymentViaFortisResponse> {
      return secured().post("/patientportal/FortisSaleWithAlias", payload);
    },

    async getFortisTransactionUrl(
      payload: FortisTransactionUrlRequest
    ): Promise<FortisTransactionUrlResponse> {
      return secured().post("/patientportal/getFortisTransactionUrl", payload);
    },

    async fortisTransactionComplete(payload: {
      facilityId: number;
      posId: number;
      email: number;
      transactionId: string;
      patientId: string;
    }): Promise<ApiResponse> {
      return secured().post("/patientportal/fortisTransactionComplete", payload);
    },

    async UpdateFortisTransactionApiId(payload: {
      facilityId: number;
      posId: number;
      transactionId: string;
    }): Promise<ApiResponse> {
      return secured().post("/patientportal/UpdateFortisTransactionApiId", payload);
    },

    async CheckPosTransactionComplete(payload: {
      facilityId: number;
      posId: number;
    }): Promise<ApiResponse> {
      return secured().post("/patientportal/CheckPosTransactionComplete", payload);
    },
  },
};

export default patientRequest;
